import React, { useRef, createContext, useState, useEffect, useCallback } from 'react';
import { getApiErrorMessage } from '@shared/utils/apiError';
import { useQueryClient } from '@tanstack/react-query';
import { clinicApi } from '../../services/clinicApi';
import type { ClinicDetailDto } from '../../types/clinic';
import type { Contact } from '../../types/contact';
import type { BasicInfo, Address, Onboarding, OperatingHour, ClinicService, ValidationErrors } from '../../types/wizard';
import { getDefaultOperatingHours } from '../../types/wizard';
import type { StepStatus, VerificationStepState } from '../../types/verification';
import { VERIFICATION_TOTAL_STEPS, VERIFICATION_STEPS } from '../../types/verification';
import { useWizardNavigation } from '../wizard/core/useWizardNavigation';

interface VerificationWizardState {
  currentStep: number;
  basicInfo: BasicInfo;
  mailingAddress: Address;
  shippingAddress: Address;
  contacts: Contact[];
  operatingHours: OperatingHour[];
  onboarding: Onboarding;
  services: ClinicService[];
  categoryTypeIds: number[];
  capabilityTypeIds: number[];
  stepStatuses: Record<number, VerificationStepState>;
}

export interface VerificationWizardContextType {
  clinic: ClinicDetailDto | null;
  isLoading: boolean;
  error: string | null;
  state: VerificationWizardState;
  stepErrors: Record<number, ValidationErrors>;

  // True when the clinic has at least one operating-hour column on record.
  // When false the hours step must say so instead of pre-filling a template
  // (SLV2-3155: a verification screen must never present invented data).
  hasHoursOnRecord: boolean;
  // Explicit user action: seed the hours form with the Mon-Fri 8-5 template.
  applyStandardHours: () => void;

  // Last failed save, as a user-facing message. Cleared on the next save
  // attempt or via clearSaveError; steps may compose their own via setSaveError.
  saveError: string | null;
  clearSaveError: () => void;
  setSaveError: (message: string | null) => void;

  // Navigation
  goToStep: (step: number) => void;
  goNext: () => Promise<boolean>;
  goPrevious: () => void;

  // Data updates
  updateBasicInfo: (data: Partial<BasicInfo>) => void;
  updateMailingAddress: (data: Partial<Address>) => void;
  updateShippingAddress: (data: Partial<Address>) => void;
  updateContacts: (contacts: Contact[]) => void;
  updateOperatingHour: (dayOfWeek: number, updates: Partial<OperatingHour>) => void;
  copyHoursToWeekdays: () => void;
  updateOnboarding: (data: Partial<Onboarding>) => void;
  updateServices: (services: ClinicService[]) => void;
  updateServiceFee: (serviceProcedureId: number, fee: number | undefined) => void;
  updateCategoryTypeIds: (ids: number[]) => void;
  updateCapabilityTypeIds: (ids: number[]) => void;

  // Step status
  markStepConfirmed: (step: number) => void;
  markStepUpdated: (step: number) => void;
  markStepSkipped: (step: number, notes?: string) => void;
  getStepStatus: (step: number) => StepStatus;

  // Actions. Each resolves true on success and false on failure (with the
  // failure surfaced via saveError) — they never reject, so callers can chain
  // them without try/catch and step statuses only advance on success.
  saveBasicInfo: () => Promise<boolean>;
  saveAddresses: () => Promise<boolean>;
  saveOperatingHours: () => Promise<boolean>;
  saveCategories: () => Promise<boolean>;
  saveCapabilityList: () => Promise<boolean>;
  saveOnboarding: () => Promise<boolean>;
  saveServices: () => Promise<boolean>;
  completeVerification: () => Promise<boolean>;

  isSaving: boolean;
  // Ref-counted hold on isSaving for multi-call save batches: a step that
  // chains several saves brackets them with begin/endSaving so the wizard's
  // navigation stays disabled for the whole sequence instead of flickering
  // enabled between awaited calls.
  beginSaving: () => void;
  endSaving: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const VerificationWizardContext = createContext<VerificationWizardContextType | undefined>(undefined);

function mapClinicToBasicInfo(clinic: ClinicDetailDto): BasicInfo {
  return {
    name: clinic.name || '',
    code: clinic.code || '',
    phone: clinic.phone || '',
    fax: clinic.fax || '',
    email: clinic.email || '',
    website: clinic.website || '',
  };
}

function mapClinicToMailingAddress(clinic: ClinicDetailDto): Address {
  return {
    address1: clinic.mailingStreet || '',
    address2: clinic.mailingSuite || '',
    city: clinic.mailingCity || '',
    state: clinic.mailingState || '',
    zip: clinic.mailingZip || '',
    country: clinic.mailingCountry || 'US',
  };
}

function mapClinicToShippingAddress(clinic: ClinicDetailDto): Address {
  return {
    address1: clinic.shippingStreet || '',
    address2: clinic.shippingSuite || '',
    city: clinic.shippingCity || '',
    state: clinic.shippingState || '',
    zip: clinic.shippingZip || '',
    country: clinic.shippingCountry || 'US',
  };
}

// All days closed — the honest representation of "no hours on record".
// The 8-5 strings are only the initial dropdown positions if a day is opened.
function getEmptyOperatingHours(): OperatingHour[] {
  return [0, 1, 2, 3, 4, 5, 6].map(dayOfWeek => ({
    dayOfWeek,
    isClosed: true,
    openTime: '08:00',
    closeTime: '17:00',
  }));
}

// Returns the clinic's hours plus whether any hours exist on record.
// When none exist, the form shows everything closed and the step displays a
// "no hours on record" notice — it must NOT pre-fill a Mon-Fri 8-5 template
// as if it were the clinic's data (SLV2-3155: "Save Changes" on an untouched
// step used to write the invented hours to the database as verified fact).
function mapClinicToOperatingHours(clinic: ClinicDetailDto): { hours: OperatingHour[]; hasHours: boolean } {
  const dayMap: { dayOfWeek: number; startKey: keyof ClinicDetailDto; endKey: keyof ClinicDetailDto }[] = [
    { dayOfWeek: 0, startKey: 'sunStart', endKey: 'sunEnd' },
    { dayOfWeek: 1, startKey: 'monStart', endKey: 'monEnd' },
    { dayOfWeek: 2, startKey: 'tueStart', endKey: 'tueEnd' },
    { dayOfWeek: 3, startKey: 'wedStart', endKey: 'wedEnd' },
    { dayOfWeek: 4, startKey: 'thurStart', endKey: 'thurEnd' },
    { dayOfWeek: 5, startKey: 'friStart', endKey: 'friEnd' },
    { dayOfWeek: 6, startKey: 'satStart', endKey: 'satEnd' },
  ];

  const hasAnyHours = dayMap.some(d => clinic[d.startKey] || clinic[d.endKey]);
  if (!hasAnyHours) return { hours: getEmptyOperatingHours(), hasHours: false };

  return {
    hasHours: true,
    hours: dayMap.map(d => {
      const start = clinic[d.startKey] as string | null;
      const end = clinic[d.endKey] as string | null;
      return {
        dayOfWeek: d.dayOfWeek,
        isClosed: !start && !end,
        openTime: start || '08:00',
        closeTime: end || '17:00',
      };
    }),
  };
}

function mapClinicToOnboarding(clinic: ClinicDetailDto): Onboarding {
  return {
    preferredMethodOfAppointment: clinic.preferredMethodOfAppointment || '',
    electronicChainOfCustody: clinic.electronicChainOfCustody || '',
    reportingMethod: clinic.reportingMethod || '',
    dotCertification: clinic.dotCertification ?? false,
    numberOfCertifiedDOTProfessionals: clinic.numberOfCertifiedDOTProfessionals ?? undefined,
    w9OnFile: clinic.w9OnFile ?? false,
    eScreenCode: clinic.eScreenCode || '',
    formFoxCode: clinic.formFoxCode || '',
    questCode: clinic.questCode || '',
  };
}

function getInitialStepStatuses(): Record<number, VerificationStepState> {
  const statuses: Record<number, VerificationStepState> = {};
  for (let i = 0; i < VERIFICATION_TOTAL_STEPS; i++) {
    statuses[i] = { status: 'pending' };
  }
  return statuses;
}

interface VerificationWizardProviderProps {
  clinicId: number;
  children: React.ReactNode;
}

export function VerificationWizardProvider({ clinicId, children }: Readonly<VerificationWizardProviderProps>) {
  const queryClient = useQueryClient();
  const loadedOnboardingRef = useRef<Onboarding | null>(null);
  const [clinic, setClinic] = useState<ClinicDetailDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Ref-counted so overlapping/batched saves don't flicker isSaving false
  // between calls (which would re-enable wizard navigation mid-batch).
  const [savingCount, setSavingCount] = useState(0);
  const isSaving = savingCount > 0;
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasHoursOnRecord, setHasHoursOnRecord] = useState(false);

  const [state, setState] = useState<VerificationWizardState>({
    currentStep: 0,
    basicInfo: { name: '' },
    mailingAddress: { address1: '', city: '', state: '', zip: '', country: 'US' },
    shippingAddress: { address1: '', city: '', state: '', zip: '', country: 'US' },
    contacts: [],
    operatingHours: getEmptyOperatingHours(),
    onboarding: {},
    services: [],
    categoryTypeIds: [],
    capabilityTypeIds: [],
    stepStatuses: getInitialStepStatuses(),
  });

  const [stepErrors] = useState<Record<number, ValidationErrors>>({});

  // Load clinic data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [clinicData, contactsData] = await Promise.all([
          clinicApi.getClinicById(clinicId),
          clinicApi.getClinicContacts(clinicId),
        ]);

        setClinic(clinicData);

        // Load services if available
        let services: ClinicService[] = [];
        try {
          const servicesData = await clinicApi.getClinicServices(clinicId);
          services = servicesData.map(s => ({
            serviceProcedureId: s.procedureId,
            name: s.procedureName || '',
            code: s.procedureCode || '',
            fee: s.fee,
          }));
        } catch {
          // Services/fees might not be enabled — that's OK
        }

        // Load dynamic category + capability selections (junction tables).
        let categoryTypeIds: number[] = [];
        let capabilityTypeIds: number[] = [];
        try {
          const [categories, capabilityList] = await Promise.all([
            clinicApi.getClinicCategories(clinicId),
            clinicApi.getClinicCapabilityList(clinicId),
          ]);
          categoryTypeIds = categories.map(c => c.id);
          capabilityTypeIds = capabilityList;
        } catch {
          // Junction lookups might be empty — that's OK
        }

        // Snapshot of the onboarding values as loaded: at save time a vendor code is
        // sent only if the user changed it here; otherwise the fresh value is carried.
        loadedOnboardingRef.current = mapClinicToOnboarding(clinicData);

        const { hours, hasHours } = mapClinicToOperatingHours(clinicData);
        setHasHoursOnRecord(hasHours);

        setState(prev => ({
          ...prev,
          basicInfo: mapClinicToBasicInfo(clinicData),
          mailingAddress: mapClinicToMailingAddress(clinicData),
          shippingAddress: mapClinicToShippingAddress(clinicData),
          contacts: contactsData,
          operatingHours: hours,
          onboarding: mapClinicToOnboarding(clinicData),
          services,
          categoryTypeIds,
          capabilityTypeIds,
        }));
      } catch (err) {
        console.error('Failed to load clinic data:', err);
        setError('Failed to load clinic data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [clinicId]);

  // Navigation via shared hook
  const setCurrentStep = useCallback((step: number) => {
    setState(prev => ({ ...prev, currentStep: step }));
  }, []);

  const { goToStep, nextStep: goNext, prevStep: goPrevious } = useWizardNavigation({
    totalSteps: VERIFICATION_TOTAL_STEPS,
    currentStep: state.currentStep,
    setCurrentStep,
  });

  // Data updates
  const updateBasicInfo = useCallback((data: Partial<BasicInfo>) => {
    setState(prev => ({ ...prev, basicInfo: { ...prev.basicInfo, ...data } }));
  }, []);

  const updateMailingAddress = useCallback((data: Partial<Address>) => {
    setState(prev => ({ ...prev, mailingAddress: { ...prev.mailingAddress, ...data } }));
  }, []);

  const updateShippingAddress = useCallback((data: Partial<Address>) => {
    setState(prev => ({ ...prev, shippingAddress: { ...prev.shippingAddress, ...data } }));
  }, []);

  const updateContacts = useCallback((newContacts: Contact[]) => {
    setState(prev => ({ ...prev, contacts: newContacts }));
  }, []);

  const updateOperatingHour = useCallback((dayOfWeek: number, updates: Partial<OperatingHour>) => {
    setState(prev => ({
      ...prev,
      operatingHours: prev.operatingHours.map(h =>
        h.dayOfWeek === dayOfWeek ? { ...h, ...updates } : h
      ),
    }));
  }, []);

  const copyHoursToWeekdays = useCallback(() => {
    setState(prev => {
      const monday = prev.operatingHours.find(h => h.dayOfWeek === 1);
      if (!monday) return prev;

      return {
        ...prev,
        operatingHours: prev.operatingHours.map(h => {
          if (h.dayOfWeek >= 2 && h.dayOfWeek <= 5) {
            return { ...h, isClosed: monday.isClosed, openTime: monday.openTime, closeTime: monday.closeTime };
          }
          return h;
        }),
      };
    });
  }, []);

  // Explicit user opt-in to the Mon-Fri 8-5 template — the only way the
  // template enters the form when the clinic has no hours on record.
  const applyStandardHours = useCallback(() => {
    setState(prev => ({ ...prev, operatingHours: getDefaultOperatingHours() }));
  }, []);

  const updateOnboarding = useCallback((data: Partial<Onboarding>) => {
    setState(prev => ({ ...prev, onboarding: { ...prev.onboarding, ...data } }));
  }, []);

  const updateServices = useCallback((services: ClinicService[]) => {
    setState(prev => ({ ...prev, services }));
  }, []);

  const updateServiceFee = useCallback((serviceProcedureId: number, fee: number | undefined) => {
    setState(prev => ({
      ...prev,
      services: prev.services.map(s =>
        s.serviceProcedureId === serviceProcedureId ? { ...s, fee } : s
      ),
    }));
  }, []);

  const updateCategoryTypeIds = useCallback((ids: number[]) => {
    setState(prev => ({ ...prev, categoryTypeIds: ids }));
  }, []);

  const updateCapabilityTypeIds = useCallback((ids: number[]) => {
    setState(prev => ({ ...prev, capabilityTypeIds: ids }));
  }, []);

  // Step status
  const markStepConfirmed = useCallback((step: number) => {
    setState(prev => ({
      ...prev,
      stepStatuses: { ...prev.stepStatuses, [step]: { status: 'confirmed' as StepStatus } },
    }));
  }, []);

  const markStepUpdated = useCallback((step: number) => {
    setState(prev => ({
      ...prev,
      stepStatuses: { ...prev.stepStatuses, [step]: { status: 'updated' as StepStatus } },
    }));
  }, []);

  const markStepSkipped = useCallback((step: number, notes?: string) => {
    setState(prev => ({
      ...prev,
      stepStatuses: { ...prev.stepStatuses, [step]: { status: 'skipped' as StepStatus, notes } },
    }));
  }, []);

  const getStepStatus = useCallback((step: number): StepStatus => {
    return state.stepStatuses[step]?.status || 'pending';
  }, [state.stepStatuses]);

  // Helper to build UpdateClinicRequest with all required fields.
  // isActive is deliberately absent: status is not part of an info update
  // (SLV2-3195) — a stale snapshot value could revert a concurrent status change.
  const buildUpdateRequest = useCallback((overrides: Record<string, unknown> = {}) => {
    return {
      name: state.basicInfo.name,
      phone: state.basicInfo.phone || null,
      fax: state.basicInfo.fax || null,
      email: state.basicInfo.email || null,
      website: state.basicInfo.website || null,
      mailingStreet: state.mailingAddress.address1 || null,
      mailingSuite: state.mailingAddress.address2 || null,
      mailingCity: state.mailingAddress.city || null,
      mailingState: state.mailingAddress.state || null,
      mailingZip: state.mailingAddress.zip || null,
      ...overrides,
    };
  }, [state.basicInfo, state.mailingAddress, clinic]);

  // Shared save wrapper (SLV2-3155): every save used to be try/finally with no
  // catch — a 403/400 became an unhandled rejection with nothing shown to the
  // user, and chained saves silently aborted mid-sequence. runSave surfaces
  // the failure via saveError, resolves false instead of rejecting, and
  // invalidates the clinic query on success so the profile reflects the
  // change without a hard refresh (the app's 5-minute staleTime otherwise
  // masks writes).
  const beginSaving = useCallback(() => setSavingCount(c => c + 1), []);
  const endSaving = useCallback(() => setSavingCount(c => Math.max(0, c - 1)), []);

  const runSave = useCallback(async (label: string, fn: () => Promise<void>): Promise<boolean> => {
    beginSaving();
    setSaveError(null);
    try {
      await fn();
      queryClient.invalidateQueries({ queryKey: ['clinic', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['clinics'] });
      return true;
    } catch (err) {
      console.error(`Failed to save ${label}:`, err);
      setSaveError(`Failed to save ${label}: ${getApiErrorMessage(err)}`);
      return false;
    } finally {
      endSaving();
    }
  }, [clinicId, queryClient, beginSaving, endSaving]);

  const clearSaveError = useCallback(() => setSaveError(null), []);

  // Save actions
  const saveBasicInfo = useCallback(() =>
    runSave('basic information', async () => {
      await clinicApi.updateClinic(clinicId, buildUpdateRequest());
      markStepUpdated(0);
    }), [runSave, clinicId, buildUpdateRequest, markStepUpdated]);

  const saveAddresses = useCallback(() =>
    runSave('addresses', async () => {
      await clinicApi.updateClinicAddress(clinicId, {
        addressType: 'Mailing',
        street: state.mailingAddress.address1,
        suite: state.mailingAddress.address2,
        city: state.mailingAddress.city,
        state: state.mailingAddress.state,
        zip: state.mailingAddress.zip,
        country: state.mailingAddress.country,
      });
      await clinicApi.updateClinicAddress(clinicId, {
        addressType: 'Shipping',
        street: state.shippingAddress.address1,
        suite: state.shippingAddress.address2,
        city: state.shippingAddress.city,
        state: state.shippingAddress.state,
        zip: state.shippingAddress.zip,
        country: state.shippingAddress.country,
      });
      markStepUpdated(1);
    }), [runSave, clinicId, state.mailingAddress, state.shippingAddress, markStepUpdated]);

  const saveOperatingHours = useCallback(() =>
    runSave('operating hours', async () => {
      const getTime = (dayOfWeek: number, type: 'open' | 'close'): string | null => {
        const hour = state.operatingHours.find(h => h.dayOfWeek === dayOfWeek);
        if (!hour || hour.isClosed) return null;
        return type === 'open' ? hour.openTime : hour.closeTime;
      };

      await clinicApi.updateOperatingHours(clinicId, {
        sunStart: getTime(0, 'open'), sunEnd: getTime(0, 'close'),
        monStart: getTime(1, 'open'), monEnd: getTime(1, 'close'),
        tueStart: getTime(2, 'open'), tueEnd: getTime(2, 'close'),
        wedStart: getTime(3, 'open'), wedEnd: getTime(3, 'close'),
        thurStart: getTime(4, 'open'), thurEnd: getTime(4, 'close'),
        friStart: getTime(5, 'open'), friEnd: getTime(5, 'close'),
        satStart: getTime(6, 'open'), satEnd: getTime(6, 'close'),
      });
      // An all-closed save writes NULL to every hour column — that is still
      // "no hours on record", so derive the flag from what was actually saved.
      setHasHoursOnRecord(state.operatingHours.some(hour => !hour.isClosed));
      markStepUpdated(VERIFICATION_STEPS.OPERATING_HOURS);
    }), [runSave, clinicId, state.operatingHours, markStepUpdated]);

  // NOTE: the three capabilities-step saves (categories, capability list,
  // onboarding) deliberately do NOT mark the step updated —
  // VerifyCapabilitiesStep chains all three and marks the step only when every
  // section succeeded, so a partial failure leaves the step honestly pending.
  // There is no capability-flags save any more: the Emergency Room / After
  // Hours / PAT / X-Ray tiles were removed (Clinic Network decision
  // 2026-09-16) and the server derives those columns from the lists.
  const saveCategories = useCallback(() =>
    runSave('categories', async () => {
      await clinicApi.updateCategories(clinicId, state.categoryTypeIds);
    }), [runSave, clinicId, state.categoryTypeIds]);

  const saveCapabilityList = useCallback(() =>
    runSave('capability list', async () => {
      await clinicApi.updateCapabilityList(clinicId, state.capabilityTypeIds);
    }), [runSave, clinicId, state.capabilityTypeIds]);

  const saveOnboarding = useCallback(() =>
    runSave('onboarding information', async () => {
      // SLV2-2999: onboarding columns only persist through the onboarding
      // endpoint — PUT /clinics/{id} silently drops these fields.
      //
      // The proc overwrites every onboarding column it covers. The vendor codes
      // are editable on this step, so send a code only if it changed since the
      // wizard loaded; otherwise carry the fresh value, or saving this step could
      // blank a code edited elsewhere while the wizard was open.
      const fresh = await clinicApi.getClinicById(clinicId);
      const loaded = loadedOnboardingRef.current;
      const codeToSend = (field: 'eScreenCode' | 'formFoxCode' | 'questCode'): string | undefined => {
        const current = state.onboarding[field] ?? '';
        return current !== (loaded?.[field] ?? '')
          ? current.trim() || undefined
          : fresh[field] ?? undefined;
      };
      await clinicApi.updateClinicOnboarding(clinicId, {
        preferredMethodOfAppointment: state.onboarding.preferredMethodOfAppointment,
        electronicChainOfCustody: state.onboarding.electronicChainOfCustody,
        reportingMethod: state.onboarding.reportingMethod,
        dotCertification: state.onboarding.dotCertification,
        numberOfCertifiedDOTProfessionals: state.onboarding.numberOfCertifiedDOTProfessionals,
        w9OnFile: state.onboarding.w9OnFile,
        eScreenCode: codeToSend('eScreenCode'),
        formFoxCode: codeToSend('formFoxCode'),
        questCode: codeToSend('questCode'),
      });
    }), [runSave, clinicId, state.onboarding]);

  const saveServices = useCallback(() =>
    runSave('services', async () => {
      await clinicApi.updateClinicServices(clinicId, {
        services: state.services.map(s => ({
          procedureId: s.serviceProcedureId,
          // SLV2-3452: blank = keep the fee on file (was forced to 0); ignored server-side for non-admins
          fee: s.fee ?? null,
        })),
      });
      // The profile's Services tab reads these keys (ClinicProceduresView) —
      // runSave only invalidates ['clinic']/['clinics'].
      queryClient.invalidateQueries({ queryKey: ['clinic-services-master', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['clinic-services', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['clinicFees', clinicId] });
      markStepUpdated(5);
    }), [runSave, clinicId, state.services, queryClient, markStepUpdated]);

  const completeVerification = useCallback(() =>
    runSave('verification', async () => {
      // Count step statuses and collect skip notes — uspVerifyClinic persists
      // these to dbo.ClinicMgmtNotes as the verification's audit record
      // (SLV2-3155; they were previously accepted and discarded).
      let stepsConfirmed = 0;
      let stepsUpdated = 0;
      let stepsSkipped = 0;
      const skipNotes: string[] = [];
      for (let i = 0; i <= 5; i++) {
        const stepState = state.stepStatuses[i];
        if (stepState?.status === 'confirmed') stepsConfirmed++;
        else if (stepState?.status === 'updated') stepsUpdated++;
        else if (stepState?.status === 'skipped') {
          stepsSkipped++;
          if (stepState.notes) skipNotes.push(`Step ${i + 1}: ${stepState.notes}`);
        }
      }

      // @Notes is NVARCHAR(500) server-side
      const notes = skipNotes.length > 0 ? skipNotes.join(' | ').slice(0, 500) : undefined;

      await clinicApi.verifyClinic(clinicId, { stepsConfirmed, stepsUpdated, stepsSkipped, notes });
      queryClient.invalidateQueries({ queryKey: ['verification-queue'] });
    }), [runSave, clinicId, state.stepStatuses, queryClient]);

  const value: VerificationWizardContextType = {
    clinic,
    isLoading,
    error,
    state,
    stepErrors,
    hasHoursOnRecord,
    applyStandardHours,
    saveError,
    clearSaveError,
    setSaveError,
    goToStep,
    goNext,
    goPrevious,
    updateBasicInfo,
    updateMailingAddress,
    updateShippingAddress,
    updateContacts,
    updateOperatingHour,
    copyHoursToWeekdays,
    updateOnboarding,
    updateServices,
    updateServiceFee,
    updateCategoryTypeIds,
    updateCapabilityTypeIds,
    markStepConfirmed,
    markStepUpdated,
    markStepSkipped,
    getStepStatus,
    saveBasicInfo,
    saveAddresses,
    saveOperatingHours,
    saveCategories,
    saveCapabilityList,
    saveOnboarding,
    saveServices,
    completeVerification,
    isSaving,
    beginSaving,
    endSaving,
  };

  return (
    <VerificationWizardContext.Provider value={value}>
      {children}
    </VerificationWizardContext.Provider>
  );
}

